import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Group,
  Loader,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowUp,
  IconChevronDown,
  IconChevronRight,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import { PageTitle } from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

function formatLabel(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ');
}

function formatSet(set) {
  const bits = [];
  const source = Number(set.prescription_source || 1);
  const isPercent = source === 3 && set.percent_of_max != null;
  if (isPercent) {
    bits.push(`${Number(set.percent_of_max)}%`);
  } else if (set.prescribed_weight != null) {
    bits.push(`${Number(set.prescribed_weight)} lb`);
  }
  if (set.prescribed_reps != null) bits.push(`${set.prescribed_reps} reps`);
  if (set.prescribed_duration_seconds != null) {
    bits.push(`${set.prescribed_duration_seconds}s`);
  }
  if (set.prescribed_distance != null) {
    const unit = set.prescribed_distance_unit || 'yd';
    bits.push(`${Number(set.prescribed_distance)} ${unit}`);
  }
  if (set.prescribed_how) bits.push(set.prescribed_how);
  if (set.prescribed_rest_seconds) bits.push(`${set.prescribed_rest_seconds}s rest`);
  if (set.estimated_rpe != null) bits.push(`RPE ${set.estimated_rpe}`);
  return bits.join(' · ') || '—';
}

function exerciseFlags(exercise) {
  return {
    usesWeight: exercise?.uses_weight ?? true,
    usesReps: exercise?.uses_reps ?? true,
    usesDuration: exercise?.uses_duration ?? false,
    usesDistance: exercise?.uses_distance ?? false,
  };
}

function flagsFromDraftRow(row) {
  return {
    usesWeight: row?.uses_weight ?? true,
    usesReps: row?.uses_reps ?? true,
    usesDuration: row?.uses_duration ?? false,
    usesDistance: row?.uses_distance ?? false,
  };
}

function blankSet(flags) {
  return {
    key: `set-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    prescribed_weight: flags.usesWeight ? 135 : null,
    percent_of_max: null,
    prescribed_reps: flags.usesReps ? 5 : null,
    prescribed_duration_seconds: flags.usesDuration ? 30 : null,
    prescribed_distance: flags.usesDistance ? 40 : null,
    prescribed_distance_unit: flags.usesDistance ? 'yd' : null,
    prescribed_how: null,
    prescribed_rest_seconds: 90,
    prescription_source: 1,
  };
}

function cloneSet(set) {
  return {
    key: `set-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    prescribed_weight: set.prescribed_weight,
    percent_of_max: set.percent_of_max,
    prescribed_reps: set.prescribed_reps,
    prescribed_duration_seconds: set.prescribed_duration_seconds,
    prescribed_distance: set.prescribed_distance,
    prescribed_distance_unit: set.prescribed_distance_unit,
    prescribed_how: set.prescribed_how,
    prescribed_rest_seconds: set.prescribed_rest_seconds,
    prescription_source: set.prescription_source || 1,
  };
}

function mapSetToDraft(set, index) {
  return {
    key: set.id || `set-${index}-${set.position ?? index}`,
    prescribed_weight: set.prescribed_weight,
    prescribed_reps: set.prescribed_reps,
    prescribed_rest_seconds: set.prescribed_rest_seconds,
    prescribed_duration_seconds: set.prescribed_duration_seconds,
    prescribed_distance: set.prescribed_distance,
    prescribed_distance_unit: set.prescribed_distance_unit,
    prescribed_how: set.prescribed_how,
    prescription_source: set.prescription_source || 1,
    percent_of_max: set.percent_of_max,
    estimated_rpe: set.estimated_rpe,
  };
}

function mapExercisesToDraft(exercises) {
  return (exercises || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((row) => {
      const exercise = row.exercise || {};
      return {
        key: row.id || `${row.exercise_id}-${row.position}`,
        exercise_id: row.exercise_id || exercise.id,
        name: exercise.name || 'Exercise',
        uses_weight: exercise.uses_weight ?? true,
        uses_reps: exercise.uses_reps ?? true,
        uses_duration: exercise.uses_duration ?? false,
        uses_distance: exercise.uses_distance ?? false,
        sets: (row.sets || [])
          .slice()
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((set, index) => mapSetToDraft(set, index)),
      };
    });
}

function swapItems(list, fromIndex, toIndex) {
  if (toIndex < 0 || toIndex >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

function isBlankNumber(value) {
  return value == null || value === '';
}

function ExerciseListReadOnly({ exercises }) {
  if (exercises.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No exercises on this workout.
      </Text>
    );
  }

  return exercises.map((exerciseRow, index) => (
    <Stack key={exerciseRow.id || index} gap={2}>
      <Text size="sm" fw={600}>
        {index + 1}. {exerciseRow.exercise?.name || 'Exercise'}
      </Text>
      {(exerciseRow.sets || []).length > 0 && (
        <Text size="xs" c="dimmed">
          {(exerciseRow.sets || [])
            .slice()
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((set) => formatSet(set))
            .join(' · ')}
        </Text>
      )}
    </Stack>
  ));
}

function exerciseSetsSummary(sets) {
  const list = sets || [];
  if (list.length === 0) return 'No sets';
  const labels = list.map((set) => formatSet(set));
  const allSame = labels.every((label) => label === labels[0]);
  if (allSame) {
    return `${list.length} × ${labels[0]}`;
  }
  return `${list.length} sets · ${labels[0]}${list.length > 1 ? '…' : ''}`;
}

function SetEditorRow({
  set,
  setIndex,
  setCount,
  flags,
  disabled,
  expanded,
  onToggle,
  onChange,
  onMove,
  onRemove,
}) {
  const source = Number(set.prescription_source || 1);
  const rxMode = source === 3 ? 'percent' : 'weight';

  function setRxMode(mode) {
    if (mode === 'percent') {
      onChange({
        prescription_source: 3,
        prescribed_weight: null,
        percent_of_max: set.percent_of_max ?? 80,
      });
    } else {
      onChange({
        prescription_source: 1,
        percent_of_max: null,
        prescribed_weight: set.prescribed_weight ?? 135,
      });
    }
  }

  return (
    <Paper p="sm" withBorder radius="sm" bg="var(--mantine-color-body)">
      <Stack gap={expanded ? 'xs' : 0}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack
            gap={2}
            style={{ minWidth: 0, flex: 1, cursor: disabled ? 'default' : 'pointer' }}
            onClick={() => {
              if (!disabled) onToggle();
            }}
          >
            <Group gap={6} wrap="nowrap">
              {expanded ? (
                <IconChevronDown size={14} />
              ) : (
                <IconChevronRight size={14} />
              )}
              <Text size="sm" fw={600}>
                Set {setIndex + 1}
              </Text>
            </Group>
            {!expanded ? (
              <Text size="xs" c="dimmed" pl={20} truncate>
                {formatSet(set)}
              </Text>
            ) : null}
          </Stack>
          <Group gap={4} wrap="nowrap" onClick={(event) => event.stopPropagation()}>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Move set ${setIndex + 1} up`}
              onClick={() => onMove(-1)}
              disabled={disabled || setIndex === 0}
            >
              <IconArrowUp size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Move set ${setIndex + 1} down`}
              onClick={() => onMove(1)}
              disabled={disabled || setIndex >= setCount - 1}
            >
              <IconArrowDown size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={`Remove set ${setIndex + 1}`}
              onClick={onRemove}
              disabled={disabled || setCount <= 1}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {expanded ? (
          <>
            {flags.usesWeight ? (
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Prescription
                </Text>
                <SegmentedControl
                  fullWidth
                  size="xs"
                  value={rxMode}
                  onChange={setRxMode}
                  disabled={disabled}
                  data={[
                    { label: 'Weight (lb)', value: 'weight' },
                    { label: '% of max', value: 'percent' },
                  ]}
                />
              </div>
            ) : null}

            <Group grow align="flex-start">
              {flags.usesWeight ? (
                rxMode === 'percent' ? (
                  <NumberInput
                    label="% of max"
                    value={set.percent_of_max}
                    onChange={(value) =>
                      onChange({
                        percent_of_max: value,
                        prescribed_weight: null,
                        prescription_source: 3,
                      })
                    }
                    min={1}
                    max={100}
                    disabled={disabled}
                  />
                ) : (
                  <NumberInput
                    label="Weight (lb)"
                    value={set.prescribed_weight}
                    onChange={(value) =>
                      onChange({
                        prescribed_weight: value,
                        percent_of_max: null,
                        prescription_source: 1,
                      })
                    }
                    min={0}
                    disabled={disabled}
                  />
                )
              ) : null}
              {flags.usesReps ? (
                <NumberInput
                  label="Reps"
                  value={set.prescribed_reps}
                  onChange={(value) => onChange({ prescribed_reps: value })}
                  min={1}
                  disabled={disabled}
                />
              ) : null}
              {flags.usesDuration ? (
                <NumberInput
                  label="Duration (sec)"
                  value={set.prescribed_duration_seconds}
                  onChange={(value) =>
                    onChange({ prescribed_duration_seconds: value })
                  }
                  min={1}
                  disabled={disabled}
                />
              ) : null}
              {flags.usesDistance ? (
                <NumberInput
                  label="Distance (yd)"
                  value={set.prescribed_distance}
                  onChange={(value) => onChange({ prescribed_distance: value })}
                  min={0}
                  decimalScale={1}
                  disabled={disabled}
                />
              ) : null}
              <NumberInput
                label="Rest (sec)"
                value={set.prescribed_rest_seconds}
                onChange={(value) => onChange({ prescribed_rest_seconds: value })}
                min={0}
                disabled={disabled}
              />
            </Group>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function AdminWorkoutDetail() {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [draftExercises, setDraftExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [expandedExerciseKey, setExpandedExerciseKey] = useState(null);
  const [editingSetKey, setEditingSetKey] = useState(null);

  const [addExerciseId, setAddExerciseId] = useState(null);
  const [addRxMode, setAddRxMode] = useState('weight');
  const [addWeight, setAddWeight] = useState(135);
  const [addPercent, setAddPercent] = useState(80);
  const [addReps, setAddReps] = useState(5);
  const [addDuration, setAddDuration] = useState(30);
  const [addDistance, setAddDistance] = useState(40);
  const [addRest, setAddRest] = useState(90);

  const workoutQuery = useQuery({
    queryKey: ['admin-workout', workoutId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/workouts/${workoutId}/`, { signal });
      return data.response?.[0];
    },
    enabled: Boolean(workoutId),
  });

  const workout = workoutQuery.data;
  const locked = Boolean(workout?.locked);

  const exercisesQuery = useQuery({
    queryKey: ['admin-exercises-picker'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-exercises/',
        { page: 0, page_size: 500, status: 2 },
        { signal },
      );
      return data.response?.[0]?.data || [];
    },
    enabled: Boolean(workout) && !locked,
  });

  useEffect(() => {
    if (!workout) return;
    setName(workout.name || '');
    setNotes(workout.notes || '');
    setDraftExercises(mapExercisesToDraft(workout.exercises));
    setExpandedExerciseKey(null);
    setEditingSetKey(null);
  }, [workout]);

  const addedExerciseIds = useMemo(() => {
    const ids = new Set();
    draftExercises.forEach((row) => {
      if (row.exercise_id != null) ids.add(String(row.exercise_id));
    });
    return ids;
  }, [draftExercises]);

  const exerciseById = useMemo(() => {
    const map = {};
    (exercisesQuery.data || []).forEach((exercise) => {
      map[String(exercise.id)] = exercise;
    });
    return map;
  }, [exercisesQuery.data]);

  const selectedExercise = addExerciseId ? exerciseById[String(addExerciseId)] : null;
  const selectedFlags = exerciseFlags(selectedExercise);

  const exerciseOptions = useMemo(
    () =>
      (exercisesQuery.data || []).map((exercise) => {
        const value = String(exercise.id);
        const alreadyAdded = addedExerciseIds.has(value);
        return {
          value,
          label: alreadyAdded
            ? `${exercise.name || exercise.id} (added)`
            : exercise.name || exercise.id,
          disabled: alreadyAdded,
        };
      }),
    [exercisesQuery.data, addedExerciseIds],
  );

  const draftSetCount = useMemo(
    () =>
      draftExercises.reduce((total, row) => total + (row.sets || []).length, 0),
    [draftExercises],
  );

  function onRemoveExercise(key) {
    setDraftExercises((prev) => prev.filter((row) => row.key !== key));
    if (expandedExerciseKey === key) {
      setExpandedExerciseKey(null);
      setEditingSetKey(null);
    }
  }

  function onToggleExercise(key) {
    setExpandedExerciseKey((prev) => {
      if (prev === key) {
        setEditingSetKey(null);
        return null;
      }
      setEditingSetKey(null);
      return key;
    });
  }

  function onToggleSet(setKey) {
    setEditingSetKey((prev) => (prev === setKey ? null : setKey));
  }

  function onMoveExercise(index, delta) {
    setDraftExercises((prev) => swapItems(prev, index, index + delta));
  }

  function onUpdateSet(exerciseKey, setIndex, patch) {
    setDraftExercises((prev) =>
      prev.map((row) => {
        if (row.key !== exerciseKey) return row;
        const sets = (row.sets || []).map((set, index) =>
          index === setIndex ? { ...set, ...patch } : set,
        );
        return { ...row, sets };
      }),
    );
  }

  function onMoveSet(exerciseKey, setIndex, delta) {
    setDraftExercises((prev) =>
      prev.map((row) => {
        if (row.key !== exerciseKey) return row;
        return {
          ...row,
          sets: swapItems(row.sets || [], setIndex, setIndex + delta),
        };
      }),
    );
  }

  function onRemoveSet(exerciseKey, setIndex) {
    setDraftExercises((prev) =>
      prev.map((row) => {
        if (row.key !== exerciseKey) return row;
        if ((row.sets || []).length <= 1) return row;
        return {
          ...row,
          sets: (row.sets || []).filter((_, index) => index !== setIndex),
        };
      }),
    );
  }

  function onAddSet(exerciseKey) {
    let nextSetKey = null;
    setDraftExercises((prev) =>
      prev.map((row) => {
        if (row.key !== exerciseKey) return row;
        const flags = flagsFromDraftRow(row);
        const sets = row.sets || [];
        const nextSet =
          sets.length > 0 ? cloneSet(sets[sets.length - 1]) : blankSet(flags);
        nextSetKey = nextSet.key;
        return { ...row, sets: [...sets, nextSet] };
      }),
    );
    setExpandedExerciseKey(exerciseKey);
    if (nextSetKey) setEditingSetKey(nextSetKey);
  }

  function onAddExercise() {
    if (!addExerciseId) return;
    if (addedExerciseIds.has(String(addExerciseId))) {
      toast.error('Exercise already on this workout');
      return;
    }

    const flags = exerciseFlags(exerciseById[String(addExerciseId)]);
    if (flags.usesWeight) {
      if (addRxMode === 'percent' && isBlankNumber(addPercent)) {
        toast.error('% of max is required');
        return;
      }
      if (addRxMode === 'weight' && isBlankNumber(addWeight)) {
        toast.error('Weight is required');
        return;
      }
    }

    const exerciseId = addExerciseId;
    const exercise = exerciseById[String(exerciseId)];
    const isPercent = flags.usesWeight && addRxMode === 'percent';
    const exerciseKey = `new-${exerciseId}-${Date.now()}`;
    const setKey = `set-new-${Date.now()}`;

    setDraftExercises((prev) => [
      ...prev,
      {
        key: exerciseKey,
        exercise_id: exerciseId,
        name: exercise?.name || 'Exercise',
        uses_weight: exercise?.uses_weight ?? true,
        uses_reps: exercise?.uses_reps ?? true,
        uses_duration: exercise?.uses_duration ?? false,
        uses_distance: exercise?.uses_distance ?? false,
        sets: [
          {
            key: setKey,
            prescribed_weight: flags.usesWeight && !isPercent ? addWeight : null,
            percent_of_max: isPercent ? addPercent : null,
            prescribed_reps: flags.usesReps ? addReps : null,
            prescribed_duration_seconds: flags.usesDuration ? addDuration : null,
            prescribed_distance: flags.usesDistance ? addDistance : null,
            prescribed_distance_unit: flags.usesDistance ? 'yd' : null,
            prescribed_rest_seconds: addRest,
            prescription_source: isPercent ? 3 : 1,
          },
        ],
      },
    ]);
    setExpandedExerciseKey(exerciseKey);
    setEditingSetKey(null);
    setAddExerciseId(null);
  }

  function validateDraftForSave() {
    if (!name.trim()) {
      toast.error('Name is required');
      return false;
    }

    for (const row of draftExercises) {
      const flags = flagsFromDraftRow(row);
      const sets = row.sets || [];
      if (sets.length === 0) {
        toast.error(`${row.name} needs at least one set`);
        return false;
      }
      for (let index = 0; index < sets.length; index += 1) {
        const set = sets[index];
        if (!flags.usesWeight) continue;
        const source = Number(set.prescription_source || 1);
        if (source === 3) {
          if (isBlankNumber(set.percent_of_max)) {
            toast.error(`${row.name} set ${index + 1}: % of max is required`);
            return false;
          }
        } else if (isBlankNumber(set.prescribed_weight)) {
          toast.error(`${row.name} set ${index + 1}: weight is required`);
          return false;
        }
      }
    }

    return true;
  }

  function onSave() {
    if (!workoutId || locked) return;
    if (!validateDraftForSave()) return;

    setSaving(true);
    axios
      .put(`/workouts/${workoutId}/`, {
        name: name.trim(),
        notes: notes.trim() || null,
        exercises: draftExercises.map((row, index) => ({
          exercise_id: row.exercise_id,
          position: index,
          sets: (row.sets || []).map((set, setIndex) => {
            const source = Number(set.prescription_source || 1);
            const isPercent = source === 3;
            return {
              position: setIndex,
              prescribed_weight: isPercent ? null : set.prescribed_weight,
              prescribed_reps: set.prescribed_reps,
              prescribed_rest_seconds: set.prescribed_rest_seconds,
              prescribed_duration_seconds: set.prescribed_duration_seconds,
              prescribed_distance: set.prescribed_distance,
              prescribed_distance_unit: set.prescribed_distance_unit,
              prescribed_how: set.prescribed_how,
              prescription_source: isPercent ? 3 : 1,
              percent_of_max: isPercent ? set.percent_of_max : null,
            };
          }),
        })),
      })
      .then(() => {
        toast.success('Workout saved');
        queryClient.invalidateQueries({ queryKey: ['admin-workout', workoutId] });
        queryClient.invalidateQueries({ queryKey: ['admin-workouts'] });
        queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  function onDuplicate() {
    if (!workoutId || !locked || duplicating) return;
    setDuplicating(true);
    axios
      .post(`/workouts/${workoutId}/duplicate/`, {})
      .then(({ data }) => {
        const copy = data.response?.[0];
        if (!copy?.id) {
          toast.error('Duplicate failed');
          return;
        }
        toast.success('Editable copy created');
        queryClient.invalidateQueries({ queryKey: ['admin-workouts'] });
        queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
        navigate(`/workouts/${copy.id}`);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setDuplicating(false));
  }

  if (workoutQuery.isLoading) {
    return (
      <Group gap="sm">
        <Loader size="sm" type="dots" />
        <Text c="dimmed">Loading workout…</Text>
      </Group>
    );
  }

  if (workoutQuery.isError || !workout) {
    return (
      <Alert variant="light" color="yellow" title="Oops!">
        {workoutQuery.isError
          ? toErrorMessage(workoutQuery.error)
          : 'Workout not found'}
      </Alert>
    );
  }

  const readOnlyExercises = (workout.exercises || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return (
    <div>
      <Group gap="xs" mb="sm">
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label="Back"
          onClick={() => navigate(-1)}
        >
          <IconArrowLeft size={18} />
        </ActionIcon>
        <Text size="sm" c="dimmed">
          Workout
        </Text>
      </Group>

      <PageTitle
        title={locked ? workout.name : name || workout.name}
        subtitle={
          locked
            ? 'Locked (edit via duplicate)'
            : 'Edit name, notes, exercises, and sets'
        }
        icon={entityIcon.workout()}
        iconColor={entityColor.workout}
        actions={
          locked ? (
            <BasicButton
              text={duplicating ? 'Duplicating…' : 'Duplicate'}
              onClick={onDuplicate}
              loading={duplicating}
              disabled={duplicating}
              color={entityColor.workout}
              variant="filled"
            />
          ) : (
            <BasicButton
              text={saving ? 'Saving…' : 'Save'}
              onClick={onSave}
              loading={saving}
              disabled={saving || !name.trim()}
              color={entityColor.workout}
              variant="filled"
            />
          )
        }
      />

      <Stack gap="md" mt="md">
        <Group gap="xs">
          <Badge size="sm" variant="light">
            {formatLabel(workout.status_formatted)}
          </Badge>
          {locked && (
            <Badge size="sm" variant="outline" color="gray">
              locked
            </Badge>
          )}
          <Text size="sm" c="dimmed">
            {(locked ? readOnlyExercises : draftExercises).length} exercises
            {locked
              ? workout.planned_set_count != null
                ? ` · ${workout.planned_set_count} sets`
                : ''
              : ` · ${draftSetCount} sets`}
          </Text>
        </Group>

        {locked ? (
          <>
            <Alert variant="light" color="gray" title="Structure locked">
              This workout was used in a session, so its structure cannot be
              edited. Duplicate creates an editable copy in the library.
            </Alert>
            <Paper p="md" withBorder radius="sm">
              <Stack gap="sm">
                {workout.notes ? (
                  <Text size="sm" c="dimmed">
                    {workout.notes}
                  </Text>
                ) : null}
                <Text fw={700}>Exercises</Text>
                <ExerciseListReadOnly exercises={readOnlyExercises} />
              </Stack>
            </Paper>
          </>
        ) : (
          <Paper p="md" withBorder radius="sm">
            <Stack gap="md">
              <TextInput
                label="Name"
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                required
              />
              <Textarea
                label="Notes"
                value={notes}
                onChange={(event) => setNotes(event.currentTarget.value)}
                minRows={2}
                autosize
              />

              <Stack gap="md">
                <Text fw={700}>Exercises</Text>
                {draftExercises.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No exercises on this workout.
                  </Text>
                ) : (
                  draftExercises.map((row, index) => {
                    const flags = flagsFromDraftRow(row);
                    const sets = row.sets || [];
                    const exerciseExpanded = expandedExerciseKey === row.key;
                    return (
                      <Paper key={row.key} p="sm" withBorder radius="sm">
                        <Stack gap={exerciseExpanded ? 'sm' : 0}>
                          <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <Stack
                              gap={2}
                              style={{
                                minWidth: 0,
                                flex: 1,
                                cursor: saving ? 'default' : 'pointer',
                              }}
                              onClick={() => {
                                if (!saving) onToggleExercise(row.key);
                              }}
                            >
                              <Group gap={6} wrap="nowrap">
                                {exerciseExpanded ? (
                                  <IconChevronDown size={14} />
                                ) : (
                                  <IconChevronRight size={14} />
                                )}
                                <Text size="sm" fw={700}>
                                  {index + 1}. {row.name}
                                </Text>
                              </Group>
                              {!exerciseExpanded ? (
                                <Text size="xs" c="dimmed" pl={20} truncate>
                                  {exerciseSetsSummary(sets)}
                                </Text>
                              ) : null}
                            </Stack>
                            <Group
                              gap={4}
                              wrap="nowrap"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={`Move ${row.name} up`}
                                onClick={() => onMoveExercise(index, -1)}
                                disabled={saving || index === 0}
                              >
                                <IconArrowUp size={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={`Move ${row.name} down`}
                                onClick={() => onMoveExercise(index, 1)}
                                disabled={
                                  saving || index >= draftExercises.length - 1
                                }
                              >
                                <IconArrowDown size={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label={`Remove ${row.name}`}
                                onClick={() => onRemoveExercise(row.key)}
                                disabled={saving}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>
                          </Group>

                          {exerciseExpanded ? (
                            <>
                              <Stack gap="xs">
                                {sets.map((set, setIndex) => (
                                  <SetEditorRow
                                    key={set.key}
                                    set={set}
                                    setIndex={setIndex}
                                    setCount={sets.length}
                                    flags={flags}
                                    disabled={saving}
                                    expanded={editingSetKey === set.key}
                                    onToggle={() => onToggleSet(set.key)}
                                    onChange={(patch) =>
                                      onUpdateSet(row.key, setIndex, patch)
                                    }
                                    onMove={(delta) =>
                                      onMoveSet(row.key, setIndex, delta)
                                    }
                                    onRemove={() => {
                                      onRemoveSet(row.key, setIndex);
                                      if (editingSetKey === set.key) {
                                        setEditingSetKey(null);
                                      }
                                    }}
                                  />
                                ))}
                              </Stack>

                              <BasicButton
                                text="Add set"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => onAddSet(row.key)}
                                disabled={saving}
                                color={entityColor.workout}
                                variant="light"
                                size="xs"
                              />
                            </>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })
                )}
              </Stack>

              <Paper p="sm" withBorder radius="sm">
                <Stack gap="sm">
                  <Text fw={600}>Add exercise</Text>
                  <Select
                    label="Exercise"
                    data={exerciseOptions}
                    value={addExerciseId}
                    onChange={setAddExerciseId}
                    searchable
                    placeholder="Pick from library…"
                    disabled={exercisesQuery.isLoading}
                  />
                  {selectedExercise && selectedFlags.usesWeight ? (
                    <div>
                      <Text size="sm" fw={500} mb={4}>
                        Prescription
                      </Text>
                      <SegmentedControl
                        fullWidth
                        value={addRxMode}
                        onChange={setAddRxMode}
                        data={[
                          { label: 'Weight (lb)', value: 'weight' },
                          { label: '% of max', value: 'percent' },
                        ]}
                      />
                    </div>
                  ) : null}
                  <Group grow>
                    {selectedExercise && selectedFlags.usesWeight ? (
                      addRxMode === 'percent' ? (
                        <NumberInput
                          label="% of max"
                          value={addPercent}
                          onChange={setAddPercent}
                          min={1}
                          max={100}
                        />
                      ) : (
                        <NumberInput
                          label="Weight (lb)"
                          value={addWeight}
                          onChange={setAddWeight}
                          min={0}
                        />
                      )
                    ) : null}
                    {(!selectedExercise || selectedFlags.usesReps) && (
                      <NumberInput
                        label="Reps"
                        value={addReps}
                        onChange={setAddReps}
                        min={1}
                      />
                    )}
                    {selectedExercise && selectedFlags.usesDuration ? (
                      <NumberInput
                        label="Duration (sec)"
                        value={addDuration}
                        onChange={setAddDuration}
                        min={1}
                      />
                    ) : null}
                    {selectedExercise && selectedFlags.usesDistance ? (
                      <NumberInput
                        label="Distance (yd)"
                        value={addDistance}
                        onChange={setAddDistance}
                        min={0}
                        decimalScale={1}
                      />
                    ) : null}
                    <NumberInput
                      label="Rest (sec)"
                      value={addRest}
                      onChange={setAddRest}
                      min={0}
                    />
                  </Group>
                  <BasicButton
                    text="Add"
                    onClick={onAddExercise}
                    disabled={!addExerciseId || saving}
                    color={entityColor.workout}
                    variant="light"
                  />
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        )}
      </Stack>
    </div>
  );
}
